import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowUpRight, Loader2, AlertTriangle, Sparkles } from "lucide-react";

const STYLE_LABELS = {
  signature: "Signature",
  cyberpunk: "Cyberpunk Neon",
  ink: "Ink & Shadow",
  vapor: "Vapor Dream",
  shonen: "Shōnen Heat",
  editorial: "Cel-Shaded Editorial",
};

export default function SharePreview() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", data: null, error: null });

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/share/anime/${token}`);
        setState({ status: "ok", data, error: null });
      } catch (e) {
        setState({
          status: "error",
          data: null,
          error: e.response?.data?.detail || "Preview not found",
        });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="font-heading font-black text-xl tracking-tighter">
            E·QUANTUM<span className="text-[var(--primary)]">.</span>
          </Link>
          <Link to="/pricing" className="btn-ghost text-xs">
            Get yours <ArrowUpRight size={14} />
          </Link>
        </div>

        {state.status === "loading" && (
          <div className="py-20 text-center">
            <Loader2 className="text-[var(--primary)] animate-spin mx-auto mb-4" size={28} />
            <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading preview…</p>
          </div>
        )}

        {state.status === "error" && (
          <div className="py-20 text-center max-w-md mx-auto" data-testid="share-not-found">
            <div className="w-14 h-14 mx-auto mb-6 border border-red-500/40 bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="text-red-400" size={22} />
            </div>
            <h1 className="font-heading font-black text-3xl tracking-tighter mb-3">
              Preview not available
            </h1>
            <p className="text-zinc-400 text-sm mb-8">{state.error}</p>
            <Link to="/pricing" className="btn-primary">
              See our offerings <ArrowUpRight size={14} />
            </Link>
          </div>
        )}

        {state.status === "ok" && state.data && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10" data-testid="share-preview-ok">
            <div className="md:col-span-7">
              <div className="border border-[var(--primary)]/40 bg-black" style={{ aspectRatio: "9/16", maxHeight: 720 }}>
                <video
                  src={`${API}/share/anime/${token}/video`}
                  controls
                  autoPlay
                  loop
                  playsInline
                  className="w-full h-full object-contain bg-black"
                  data-testid="share-video"
                />
              </div>
            </div>
            <div className="md:col-span-5 flex flex-col justify-center">
              <div className="tag mb-4">
                <Sparkles size={12} /> Signature Performance Video · Preview
              </div>
              <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
                {state.data.track_title || "A signature performance video"}
              </h1>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[var(--primary)] mb-6">
                Style · {STYLE_LABELS[state.data.style] || state.data.style}
              </p>
              <p className="text-zinc-400 text-base leading-relaxed mb-8">
                A short-form teaser. The full signature performance video is
                delivered ready for every platform.
              </p>
              <Link to="/pricing" className="btn-primary justify-center" data-testid="share-cta-pricing">
                See pricing <ArrowUpRight size={16} />
              </Link>
              <p className="mt-6 text-[10px] text-zinc-600 font-mono uppercase tracking-widest">
                // Powered by E-Quantum Consults
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
